import {
    RouterState,
    RouterStore,
    createRouterState
} from 'mobx-state-router';
// import { HttpConnectorAdapter } from '../adapters';
import { RootStoreBase } from './root.store.base';
import { AdminStore, MainTabs } from './admin.store';
import Bugsnag from '@bugsnag/js';
import { RegisteredUser } from '../models/registered-user';
import { LoginWizardSteps } from '../shared/components/login-wizard';

// Routes are matched from top to bottom. Make sure they are sequenced
// in the order of priority. It is generally best to sort them by pattern,
// prioritizing specific patterns over generic patterns (patterns with
// one or more parameters). For example:
//     /items
//     /items/:id


export const ROUTE_NAMES = {
    NOT_FOUND: 'not-found',
    HOME: 'home',
    LOGIN: 'login',
    MARKERS: 'markers',
    QR_CODES: 'qr',
    QR_CODES_WITH_PARAM: 'qr-param',
    MARKER_EXPORT: 'marker-export',
    TEAM_INFO: 'team-info',
    USER_EXPORT: 'user-export',
    MARKER_OPERATIONS: 'marker-operations',
    MARKER_DETAIL: 'marker-detail',
    TEAMS: 'teams',
    FOLDERS: 'folders',
    ACCOUNTS: 'accounts',
    MODULE_GROUPS: 'module-groups',
    FLOORPLAN_REPLACE: 'floorplan-replace',
    SCHEDULING_PERMISSIONS: 'scheduling-permissions',
    MAGIC_URL: 'magic-url',
    USER_CREATION: 'user-creation',
    REPORTING_PERMISSIONS: 'reporting-permissions',
    REDIRECT: 'redirect',
}

// Page router states
const loginPageState = createRouterState(ROUTE_NAMES.LOGIN, {});
const homePageState = createRouterState(ROUTE_NAMES.HOME, {});

// const checkForUserSignedIn = (fromState: RouterState, toState: RouterState, routerStore: RouterStore) => {
//     // Redirect to Login Page if the user isn't logged in
//     const {
//         rootStore: {
//             adminStore
//         }
//     }: { rootStore: RootStoreBase } = routerStore;
//     const {
//         isUserLoggedIn,
//         loginCredentials,
//         userIdentifiers
//     } = adminStore;
//     if (!isUserLoggedIn || !loginCredentials || !userIdentifiers) {
//         // Save the redirection state
//         adminStore.setLogInRedirect(toState);

//         // Redirects to the login page 
//         return Promise.reject(new RouterState(ROUTE_NAMES.LOGIN));
//     }
//     // HttpConnectorAdapter.setCredentials(loginCredentials);
//     // HttpConnectorAdapter.setUserIdentifiers(userIdentifiers);
//     return Promise.resolve();
// }

function checkIfUserValidationIsNeeded(managerStore: AdminStore) {
    const { loggedInUser } = managerStore;
    if (loggedInUser?.isVerificationNeeded === true) {
        managerStore.setCurrentLoginWizardStep(LoginWizardSteps.ValidateAccount);
    }
}

function checkLogin (fromState: RouterState, toState: RouterState, routerStore: RouterStore): Promise<RouterState | void> {
    const { rootStore } = routerStore.options;
    const { adminStore } = rootStore as RootStoreBase;
    const { loggedInUser } = adminStore;

    // Redirect to Login Page if the user isn't logged in
    if (!loggedInUser || (loggedInUser.isVerificationNeeded === true)) {
        checkIfUserValidationIsNeeded(adminStore);
        return Promise.resolve(loginPageState);
    }
    return Promise.resolve();
}

const openQrCodePage = (floorPlanId: string, routerStore: RouterStore) => {
    const { rootStore } = routerStore.options;
    const { adminStore } = rootStore as RootStoreBase;
    const { setFloorPlanId, changeCurrentTab } = adminStore;

    setFloorPlanId(floorPlanId);
    changeCurrentTab(MainTabs.QR_Code);

    return Promise.resolve();
}

export const routes = [
    {
        name: ROUTE_NAMES.HOME,
        pattern: '/',
        beforeEnter: (fromState: RouterState, toState: RouterState, routerStore: RouterStore): Promise<RouterState | void> => {
            // If we reach this point we check if the user is logged in and
            // we will go to the main page.
            return checkLogin(fromState, toState, routerStore);
        },
        onEnter: (fromState: RouterState, toState: RouterState, routerStore: RouterStore): Promise<void> => {
            const { rootStore } = routerStore.options;
            const { adminStore } = rootStore as RootStoreBase;
            const { changeCurrentTab } = adminStore;

            changeCurrentTab(MainTabs.AccountInfo);

            return Promise.resolve();
        }
    },
    {
        name: ROUTE_NAMES.REDIRECT,
        pattern: '/redirect',
        beforeEnter: (fromState: RouterState, toState: RouterState, routerStore: RouterStore): Promise<RouterState | void> => {
            const {
                queryParams: {
                    code,
                    state
                }
            }: {
                queryParams: {code?: string, state?: string}
            } = toState;
            const { rootStore } = routerStore.options;
            const { adminStore } = rootStore as RootStoreBase;

            if (!code){
                Bugsnag.notify((new Error('Redirect issue')), (event) => {
                    event.severity = 'info';
                    event.context = 'Routing component';
                    event.addMetadata('routes qr code', {
                        fromState,
                        toState,
                        error: 'no code in redirect url'
                    });
                });


                return Promise.resolve(loginPageState);
            }

            return new Promise((resolve) => {
                adminStore
                    .loginSSO(code, state)
                    .then((registeredUser: RegisteredUser) => {
                        if (registeredUser.valid) {
                            return resolve(homePageState);
                        }
                        return resolve(loginPageState);
                    }).catch(() => {
                        return resolve(loginPageState);
                    });
            });
        }
    },
    {
        name: ROUTE_NAMES.MARKERS,
        pattern: `/${ROUTE_NAMES.MARKERS}`,
        beforeEnter: checkLogin,
        onEnter: (fromState: RouterState, toState: RouterState, routerStore: RouterStore): Promise<void> => {
            const { rootStore } = routerStore.options;
            const { adminStore } = rootStore as RootStoreBase;
            const { changeCurrentTab } = adminStore;
            changeCurrentTab(MainTabs.Markers);

            return Promise.resolve();
        }
    },
    {
        name: ROUTE_NAMES.QR_CODES,
        pattern: `/${ROUTE_NAMES.QR_CODES}`,
        beforeEnter: checkLogin,
        onEnter: (fromState: RouterState, toState: RouterState, routerStore: RouterStore): Promise<void> => {
            return openQrCodePage('', routerStore);
        }
    },
    {
        name: ROUTE_NAMES.QR_CODES_WITH_PARAM,
        pattern: `/${ROUTE_NAMES.QR_CODES}/:floorPlanId`,
        beforeEnter: checkLogin,
        onEnter: (fromState: RouterState, toState: RouterState, routerStore: RouterStore): Promise<void> => {
            const {
                floorPlanId
            } = toState.params;

            return openQrCodePage(floorPlanId, routerStore);
        }
    },
    {
        name: ROUTE_NAMES.NOT_FOUND,
        pattern: `/${ROUTE_NAMES.NOT_FOUND}`
    },
    {
        name: ROUTE_NAMES.LOGIN,
        pattern: `/${ROUTE_NAMES.LOGIN}`,
        onEnter: (fromState: RouterState, toState: RouterState, routerStore: RouterStore): Promise<void> => {
            // TO-DO: The store stuff for the page
            return Promise.resolve();
        }
    },
    {
        name: ROUTE_NAMES.MARKER_EXPORT,
        pattern: `/${ROUTE_NAMES.MARKER_EXPORT}`,
        beforeEnter: checkLogin,
        onEnter: (fromState: RouterState, toState: RouterState, routerStore: RouterStore): Promise<void> => {
            const { rootStore } = routerStore.options;
            const { adminStore } = rootStore as RootStoreBase;
            const { changeCurrentTab } = adminStore;
            changeCurrentTab(MainTabs.Marker_Export);

            return Promise.resolve();
        }
    },
    {
        name: ROUTE_NAMES.TEAM_INFO,
        pattern: `/${ROUTE_NAMES.TEAM_INFO}`,
        beforeEnter: checkLogin,
        onEnter: (fromState: RouterState, toState: RouterState, routerStore: RouterStore): Promise<void> => {
            const { rootStore } = routerStore.options;
            const { adminStore } = rootStore as RootStoreBase;
            const { changeCurrentTab } = adminStore;
            changeCurrentTab(MainTabs.Team_Info);

            return Promise.resolve();
        }
    },
    {
        name: ROUTE_NAMES.USER_EXPORT,
        pattern: `/${ROUTE_NAMES.USER_EXPORT}`,
        beforeEnter: checkLogin,
        onEnter: (fromState: RouterState, toState: RouterState, routerStore: RouterStore): Promise<void> => {
            const { rootStore } = routerStore.options;
            const { adminStore } = rootStore as RootStoreBase;
            const { changeCurrentTab } = adminStore;
            changeCurrentTab(MainTabs.User_Export);

            return Promise.resolve();
        }
    },
    {
        name: ROUTE_NAMES.MARKER_OPERATIONS,
        pattern: `/${ROUTE_NAMES.MARKER_OPERATIONS}`,
        beforeEnter: checkLogin,
        onEnter: (fromState: RouterState, toState: RouterState, routerStore: RouterStore): Promise<void> => {
            const { rootStore } = routerStore.options;
            const { adminStore } = rootStore as RootStoreBase;
            const { changeCurrentTab } = adminStore;
            changeCurrentTab(MainTabs.Marker_Operations);

            return Promise.resolve();
        }
    },
    {
        name: ROUTE_NAMES.MARKER_DETAIL,
        pattern: `/${ROUTE_NAMES.MARKER_DETAIL}`,
        beforeEnter: checkLogin,
        onEnter: (fromState: RouterState, toState: RouterState, routerStore: RouterStore): Promise<void> => {
            const { rootStore } = routerStore.options;
            const { adminStore } = rootStore as RootStoreBase;
            const { changeCurrentTab } = adminStore;
            changeCurrentTab(MainTabs.Marker_Detail);

            return Promise.resolve();
        }
    },
    {
        name: ROUTE_NAMES.TEAMS,
        pattern: `/${ROUTE_NAMES.TEAMS}`,
        beforeEnter: checkLogin,
        onEnter: (fromState: RouterState, toState: RouterState, routerStore: RouterStore): Promise<void> => {
            const { rootStore } = routerStore.options;
            const { adminStore } = rootStore as RootStoreBase;
            const { changeCurrentTab } = adminStore;
            changeCurrentTab(MainTabs.Teams);

            return Promise.resolve();
        }
    },
    {
        name: ROUTE_NAMES.FOLDERS,
        pattern: `/${ROUTE_NAMES.FOLDERS}`,
        beforeEnter: checkLogin,
        onEnter: (fromState: RouterState, toState: RouterState, routerStore: RouterStore): Promise<void> => {
            const { rootStore } = routerStore.options;
            const { adminStore } = rootStore as RootStoreBase;
            const { changeCurrentTab } = adminStore;
            changeCurrentTab(MainTabs.Folders);

            return Promise.resolve();
        }
    },
    {
        name: ROUTE_NAMES.ACCOUNTS,
        pattern: `/${ROUTE_NAMES.ACCOUNTS}`,
        beforeEnter: checkLogin,
        onEnter: (fromState: RouterState, toState: RouterState, routerStore: RouterStore): Promise<void> => {
            const { rootStore } = routerStore.options;
            const { adminStore } = rootStore as RootStoreBase;
            const { changeCurrentTab } = adminStore;
            changeCurrentTab(MainTabs.Accounts);

            return Promise.resolve();
        }
    },
    {
        name: ROUTE_NAMES.FLOORPLAN_REPLACE,
        pattern: `/${ROUTE_NAMES.FLOORPLAN_REPLACE}`,
        beforeEnter: checkLogin,
        onEnter: (fromState: RouterState, toState: RouterState, routerStore: RouterStore): Promise<void> => {
            const { rootStore } = routerStore.options;
            const { adminStore } = rootStore as RootStoreBase;
            const { changeCurrentTab } = adminStore;
            changeCurrentTab(MainTabs.Floorplan_Replace);

            return Promise.resolve();
        }
    },
    {
        name: ROUTE_NAMES.MODULE_GROUPS,
        pattern: `/${ROUTE_NAMES.MODULE_GROUPS}`,
        beforeEnter: checkLogin,
        onEnter: (fromState: RouterState, toState: RouterState, routerStore: RouterStore): Promise<void> => {
            const { rootStore } = routerStore.options;
            const { adminStore } = rootStore as RootStoreBase;
            const { changeCurrentTab } = adminStore;
            changeCurrentTab(MainTabs.Module_Groups);

            return Promise.resolve();
        }
    },
    {
        name: ROUTE_NAMES.SCHEDULING_PERMISSIONS,
        pattern: `/${ROUTE_NAMES.SCHEDULING_PERMISSIONS}`,
        beforeEnter: checkLogin,
        onEnter: (fromState: RouterState, toState: RouterState, routerStore: RouterStore): Promise<void> => {
            const { rootStore } = routerStore.options;
            const { adminStore } = rootStore as RootStoreBase;
            const { changeCurrentTab } = adminStore;
            changeCurrentTab(MainTabs.Scheduling_Permissions);

            return Promise.resolve();
        }
    },
    {
        name: ROUTE_NAMES.MAGIC_URL,
        pattern: `/${ROUTE_NAMES.MAGIC_URL}`,
        beforeEnter: checkLogin,
        onEnter: (fromState: RouterState, toState: RouterState, routerStore: RouterStore): Promise<void> => {
            const { rootStore } = routerStore.options;
            const { adminStore } = rootStore as RootStoreBase;
            const { changeCurrentTab } = adminStore;
            changeCurrentTab(MainTabs.MagicURL);

            return Promise.resolve();
        }
    },
    { 
        name: ROUTE_NAMES.USER_CREATION,
        pattern: `/${ROUTE_NAMES.USER_CREATION}`,
        beforeEnter: checkLogin,
        onEnter: (fromState: RouterState, toState: RouterState, routerStore: RouterStore): Promise<void> => {
            const { rootStore } = routerStore.options;
            const { adminStore } = rootStore as RootStoreBase;
            const { changeCurrentTab } = adminStore;
            changeCurrentTab(MainTabs.User_Creation);

            return Promise.resolve();
        }
    },
    {
        name: ROUTE_NAMES.REPORTING_PERMISSIONS,
        pattern: `/${ROUTE_NAMES.REPORTING_PERMISSIONS}`,
        beforeEnter: checkLogin,
        onEnter: (fromState: RouterState, toState: RouterState, routerStore: RouterStore): Promise<void> => {
            const { rootStore } = routerStore.options;
            const { adminStore } = rootStore as RootStoreBase;
            const { changeCurrentTab } = adminStore;
            changeCurrentTab(MainTabs.Reporting_Permissions);

            return Promise.resolve();
        }
    },
];
